import {DOCUMENT_TYPE} from "utils/constants";
import {formatAmountValue, shouldShowForeignAmount} from "utils/valueFormatter.function";
import type {MyTask, TaskWrapper, Translate} from "types/myTasks";

/**
 * Task amounts: the figure shown on one task, and the totals summed over a list of them.
 * Pure functions so they can be unit-tested without the component.
 */

export interface CurrencyTotal {
    currency: string;
    amount: number;
}

export interface TaskAmount {
    amount: string;
    unit: string;
    foreignAmount: string | null;
    foreignCurrency: string | null;
}

/**
 * totals split per currency, so amounts in different currencies are never added up.
 * A task counts with its own amount and, like the task row itself, with its foreign
 * amount when the document is in another currency. Tasks carrying no currency
 * (the days/hours based ones) keep their own entry, last.
 * @return in currency order
 */
export function sumAmountByCurrency(wrappers: TaskWrapper[]): CurrencyTotal[] {
    const byCurrency = new Map<string, number>();

    function add(currency: string, value: number) {
        const amount = Number(value);
        if (isNaN(amount)) return;
        const key = currency || '';
        byCurrency.set(key, (byCurrency.get(key) || 0) + amount);
    }

    wrappers.forEach(({task}) => {
        add(task.currency, task.amount);
        // same money as `amount` when the currencies match, so it would count twice
        if (task.foreignCurrency && task.foreignCurrency !== task.currency) {
            add(task.foreignCurrency, task.foreignAmount);
        }
    });

    // the currency-less entry goes last, the rest alphabetically
    function byCurrencyName(a: string, b: string) {
        if (a === '') return 1;
        if (b === '') return -1;
        return a.localeCompare(b);
    }

    return [...byCurrency.keys()]
        .sort(byCurrencyName)
        .map(currency => ({currency, amount: byCurrency.get(currency)}));
}

// the pending and group-row figures are a plain NOK sum - nothing is converted into them
export const TOTALS_CURRENCY = 'NOK';

/**
 * only tasks whose own amount is in NOK count, everything else (other currencies,
 * foreign amounts) is left out
 * @return empty when nothing is in NOK
 */
export function sumTotalAmount(wrappers: TaskWrapper[]): CurrencyTotal[] {
    const inTotalsCurrency = wrappers.filter(w => w.task.currency === TOTALS_CURRENCY);
    return sumAmountByCurrency(inTotalsCurrency).filter(total => total.currency === TOTALS_CURRENCY);
}

/**
 * the amount of one task, the way the task list has always shown it: leave requests count
 * days, timesheets hours, everything else money - and the foreign amount is dropped when it
 * would just repeat the main one
 */
export function normalizeTaskAmount(task: MyTask, translate: Translate): TaskAmount {
    let unit: string, amount: number, foreignUnit: string, foreignAmount: number;

    if (task.documentType === DOCUMENT_TYPE.LEAVE_REQUEST) {
        amount = task.numberOfDays;
        unit = translate('myTasks.task.amountInDays');
    } else if (task.documentType === DOCUMENT_TYPE.TIMESHEET) {
        amount = task.amount;
        unit = translate('myTasks.task.amountInHours');
        foreignAmount = task.foreignAmount;
        foreignUnit = unit;
    } else {
        amount = task.amount;
        unit = task.currency;
        foreignAmount = task.foreignAmount;
        foreignUnit = task.foreignCurrency;
    }

    const status = shouldShowForeignAmount(amount, unit, foreignAmount, foreignUnit);

    return {
        amount: formatAmountValue(status.amount),
        unit: status.unit,
        foreignAmount: status.foreignAmount ? formatAmountValue(status.foreignAmount) : null,
        foreignCurrency: status.foreignUnit
    };
}

/**
 * one total as shown to the user, e.g. "300.00 NOK"
 */
export function formatTotal(total: CurrencyTotal): string {
    const amount = formatAmountValue(total.amount);
    return total.currency ? amount + ' ' + total.currency : amount;
}
